/**
 * Master Product Selection
 *
 * The one rule for which Design Product is the master, the product whose
 * positions the seller edits and every other product derives from. Used by
 * the web design store, the API (save, product removal, agent clone) and
 * the normalisation script, so all of them pick the same product.
 *
 * Order: a forced product (single-product edit), then the persisted
 * `masterProductId`, then the heuristic from CONCEPTS.md: products with
 * a stored placement (`placementMm`), enabled first, without Custom Placement first, then by what
 * the others would inherit: a textile without a percent, a textile with a
 * percent, an object, and last a product whose areas are all KLEIN or
 * RUNDUM; oldest first, product id as the final tie-break.
 *
 * An object master gives every textile the 80 % rule and a percent master
 * hands on a box, not the seller's placement; a product without a class
 * ranks with the textiles (today's behaviour).
 */

import {
  effectiveSizeTier,
  type SizeTier,
  type SizeTierFields,
} from '../placement/sizeTier.js';
import type { ProductClass } from './carryOverDefaults.js';
import { isPlacementMm } from '../placement/placementMm.js';

export interface MasterCandidatePosition {
  /**
   * The stored placement in mm; only a position with one can be master.
   * Required so a caller cannot forget to pass it (the rule would then
   * find no master at all).
   */
  placementMm: unknown;
  isCustomPlacement?: boolean | null;
  /** Effective tier of this position's print area, when known */
  sizeTier?: SizeTier | null;
  /** Percent set on this position's print area (an area exception) */
  carryOverPercent?: number | null;
}

export interface MasterCandidate {
  productId: string;
  isEnabled: boolean;
  createdAt?: string | Date | null;
  positions?: MasterCandidatePosition[] | null;
  /** Class and product-level percent, when known */
  productClass?: ProductClass | null;
  carryOverPercent?: number | null;
}

export interface SelectMasterProductOptions {
  /** Persisted reference (`Design.masterProductId`, a Product id) */
  masterProductId?: string | null;
  /** Force a specific product regardless of state (single-product edit) */
  forceProductId?: string | null;
}

/**
 * A position that carries a placement the other products can be derived
 * from (S6): every writer stores `placementMm` with the design, so a row
 * without one has nothing to hand on.
 */
export function hasPositionDesign(pos: MasterCandidatePosition): boolean {
  return isPlacementMm(pos.placementMm);
}

function hasCustomPosition(p: MasterCandidate): boolean {
  return p.positions?.some((pos) => pos.isCustomPlacement === true) ?? false;
}

/**
 * True when every designed position of the product sits on an area the
 * carry-over maximises onto (KLEIN or RUNDUM). Such an area makes a poor
 * master: what the others would inherit is relative to a mug or a strip.
 */
function isMaximisedProduct(p: MasterCandidate): boolean {
  const designed = (p.positions ?? []).filter(hasPositionDesign);
  return (
    designed.length > 0 &&
    designed.every(
      (pos) => pos.sizeTier === 'KLEIN' || pos.sizeTier === 'RUNDUM'
    )
  );
}

/** Whether the product, or any of its designed areas, carries a percent. */
function hasCarryOverPercent(p: MasterCandidate): boolean {
  if (p.carryOverPercent != null) return true;
  return (p.positions ?? [])
    .filter(hasPositionDesign)
    .some((pos) => pos.carryOverPercent != null);
}

/**
 * Rank by what the other products would inherit from this master: lower is
 * better. KLEIN/RUNDUM-only products rank last whatever their class.
 */
function masterRank(p: MasterCandidate): number {
  if (isMaximisedProduct(p)) return 3;
  if (p.productClass === 'OBJECT') return 2;
  return hasCarryOverPercent(p) ? 1 : 0;
}

function createdAtMs(p: MasterCandidate): number {
  if (!p.createdAt) return Number.POSITIVE_INFINITY;
  const t =
    p.createdAt instanceof Date
      ? p.createdAt.getTime()
      : Date.parse(p.createdAt);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

export function selectMasterProduct<T extends MasterCandidate>(
  products: T[],
  options: SelectMasterProductOptions = {}
): T | null {
  if (options.forceProductId) {
    const forced = products.find((p) => p.productId === options.forceProductId);
    if (forced) return forced;
  }

  if (options.masterProductId) {
    const referenced = products.find(
      (p) => p.productId === options.masterProductId
    );
    if (referenced) return referenced;
  }

  const withPositions = products.filter((p) =>
    p.positions?.some(hasPositionDesign)
  );
  if (withPositions.length === 0) return null;

  const enabled = withPositions.filter((p) => p.isEnabled);
  let pool = enabled.length > 0 ? enabled : withPositions;

  const nonCustom = pool.filter((p) => !hasCustomPosition(p));
  if (nonCustom.length > 0) pool = nonCustom;

  const bestRank = Math.min(...pool.map(masterRank));
  pool = pool.filter((p) => masterRank(p) === bestRank);

  return pool.reduce((best, p) => {
    const pMs = createdAtMs(p);
    const bestMs = createdAtMs(best);
    if (pMs !== bestMs) return pMs < bestMs ? p : best;
    return p.productId < best.productId ? p : best;
  });
}

/** A Print Area row as the API selects it for the master choice. */
export interface MasterCandidateArea extends SizeTierFields {
  carryOverPercent?: number | null;
}

/** A DesignPosition row as the API selects it, with its Print Area. */
export interface MasterCandidateRow {
  /**
   * The stored placement; required so a caller cannot forget to select it
   * (a row without it never counts, and the rule would pick no master).
   */
  placementMm: unknown;
  isCustomPlacement?: boolean | null;
  productPosition?: { printArea?: MasterCandidateArea | null } | null;
}

/** A DesignProduct row as the API selects it. */
export interface MasterCandidateProduct {
  productId: string;
  isEnabled: boolean;
  createdAt?: string | Date | null;
  positions: readonly MasterCandidateRow[];
  /**
   * The catalogue product, when selected (class and percent). Callers select
   * other product columns too; those pass through untyped.
   */
  product?: {
    productClass?: ProductClass | null;
    carryOverPercent?: number | null;
    [column: string]: unknown;
  } | null;
}

/**
 * The master's product id straight from API rows: reads each position's
 * effective tier from its Print Area and applies the rule above. Used by
 * the product removal and the agent clone, so both read the same master
 * without repeating the mapping.
 */
function toCandidate(product: MasterCandidateProduct): MasterCandidate {
  return {
    productId: product.productId,
    isEnabled: product.isEnabled,
    createdAt: product.createdAt,
    productClass: product.product?.productClass,
    carryOverPercent: product.product?.carryOverPercent,
    positions: product.positions.map((pos) => ({
      placementMm: pos.placementMm,
      isCustomPlacement: pos.isCustomPlacement,
      sizeTier: pos.productPosition?.printArea
        ? effectiveSizeTier(pos.productPosition.printArea)
        : undefined,
      carryOverPercent: pos.productPosition?.printArea?.carryOverPercent,
    })),
  };
}

export function selectMasterProductId(
  products: readonly MasterCandidateProduct[],
  options: SelectMasterProductOptions = {}
): string | null {
  const master = selectMasterProduct(products.map(toCandidate), options);
  return master?.productId ?? null;
}

/**
 * Whether every designed area of the product is KLEIN or RUNDUM. Such a
 * product is never pinned as master, even when it is the only product of
 * the design: the pin would survive the shirt added later, which would
 * then derive from a maximised state (see `isMaximisedProduct`).
 */
export function isMaximisedOnlyProduct(
  product: MasterCandidateProduct
): boolean {
  return isMaximisedProduct(toCandidate(product));
}

/**
 * THE master of a design as every reader must resolve it: the persisted
 * `Design.masterProductId` when it points at one of the design's products,
 * otherwise the heuristic above. One function for the freeze, the web load,
 * the impact counter, the configuration command and the placement diff, so
 * none of them picks a different master than the save path did.
 */
export function resolveMasterProductId(
  products: readonly MasterCandidateProduct[],
  masterProductId: string | null | undefined
): string | null {
  return selectMasterProductId(products, { masterProductId });
}
